import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, CheckCircle, Plus, Minus, ChevronRight, AlertCircle } from 'lucide-react';
import Button from '../ui/Button';
import Card from '../ui/Card';

/**
 * Pantalla de instrucciones del Test DISC
 * Explica el formato de respuesta con + (más) y - (menos)
 * Incluye ejemplo de práctica
 */
const PantallaInstruccionesDisc = ({ onIniciar }) => {
  const [paso, setPaso] = useState(0); // 0: Instrucciones, 1: Ejemplo práctico, 2: Confirmación final
  const [respuestaMas, setRespuestaMas] = useState(null);
  const [respuestaMenos, setRespuestaMenos] = useState(null);

  // Palabras de ejemplo
  const palabrasEjemplo = [
    { id: 1, texto: 'Enérgico', dimension: 'D' },
    { id: 2, texto: 'Persuasivo', dimension: 'I' },
    { id: 3, texto: 'Paciente', dimension: 'S' },
    { id: 4, texto: 'Analítico', dimension: 'C' }
  ];

  const handleSeleccionMas = (id) => {
    if (respuestaMenos === id) return; // No permitir si ya está en menos
    setRespuestaMas(id);
  };

  const handleSeleccionMenos = (id) => {
    if (respuestaMas === id) return; // No permitir si ya está en más
    setRespuestaMenos(id);
  };

  const resetEjemplo = () => {
    setRespuestaMas(null);
    setRespuestaMenos(null);
  };

  const puedeAvanzar = respuestaMas !== null && respuestaMenos !== null;

  // Pantalla de confirmación final
  if (paso === 2) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl w-full"
        >
          <Card className="border-t-4 border-t-purple-600">
            <div className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-purple-600" />
              </div>
              
              <h2 className="text-3xl font-bold text-neutral-800 mb-4">
                ¡Estás Listo para Comenzar!
              </h2>
              
              <p className="text-lg text-neutral-600 mb-6">
                Has completado la práctica. El test real consta de <strong>28 grupos de características</strong> donde deberás seleccionar la que MÁS y MENOS te describe.
              </p>

              <div className="bg-purple-50 rounded-lg p-6 border border-purple-200 mb-8">
                <div className="space-y-3 text-left">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <p className="text-neutral-700">
                      <strong>Recuerda:</strong> Responde con honestidad, no hay respuestas incorrectas
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <p className="text-neutral-700">
                      <strong>Primera impresión:</strong> No pienses demasiado cada respuesta
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <p className="text-neutral-700">
                      <strong>Sé tú mismo:</strong> Responde cómo eres, no cómo quisieras ser
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 border-2 border-green-300 mb-8">
                <p className="text-2xl font-bold text-green-800 mb-2">
                  ¡Mucha Suerte!
                </p>
                <p className="text-green-700">
                  Da clic en Continuar para comenzar el test oficial
                </p>
              </div>

              <Button
                onClick={onIniciar}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-lg py-4"
              >
                <ChevronRight className="w-6 h-6 mr-2" />
                Continuar - Comenzar Test Real
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    );
  }

  if (paso === 1) {
    // Pantalla de ejemplo práctico - FORMATO IGUAL AL EXAMEN REAL
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          className="max-w-4xl w-full"
        >
          <Card className="border-t-4 border-t-purple-600 p-6">
            <div className="space-y-6">
              {/* Header */}
              <div className="text-center space-y-2">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-3">
                  <span className="text-purple-600 font-bold text-lg">Ejemplo</span>
                </div>
                <h2 className="text-xl font-bold text-neutral-800">
                  Grupo de Práctica
                </h2>
                <p className="text-neutral-600">
                  De las siguientes características, selecciona:
                </p>
                <div className="flex justify-center gap-8 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-green-500"></div>
                    <span className="text-sm font-medium text-neutral-700">La que MÁS te describe</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-red-500"></div>
                    <span className="text-sm font-medium text-neutral-700">La que MENOS te describe</span>
                  </div>
                </div>
              </div>

              {/* Opciones - FORMATO IDÉNTICO AL EXAMEN REAL */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                {palabrasEjemplo.map((palabra) => {
                  const seleccionadaMas = respuestaMas === palabra.id;
                  const seleccionadaMenos = respuestaMenos === palabra.id;
                  const deshabilitada = (respuestaMas && !seleccionadaMas && !seleccionadaMenos) ||
                                       (respuestaMenos && !seleccionadaMas && !seleccionadaMenos);

                  return (
                    <div
                      key={palabra.id}
                      className={`
                        relative p-6 rounded-lg border-2 transition-all duration-200
                        ${seleccionadaMas ? 'border-green-500 bg-green-50' : ''}
                        ${seleccionadaMenos ? 'border-red-500 bg-red-50' : ''}
                        ${!seleccionadaMas && !seleccionadaMenos ? 'border-neutral-200 hover:border-purple-300 hover:bg-neutral-50' : ''}
                        ${deshabilitada ? 'opacity-40' : ''}
                      `}
                    >
                      {/* Texto de la opción */}
                      <div className="text-center mb-4">
                        <span className="text-lg font-semibold text-neutral-800">
                          {palabra.texto}
                        </span>
                      </div>

                      {/* Botones MÁS / MENOS */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSeleccionMas(palabra.id)}
                          disabled={respuestaMenos === palabra.id}
                          className={`
                            flex-1 py-2 px-4 rounded-md font-medium transition-all
                            ${seleccionadaMas 
                              ? 'bg-green-500 text-white shadow-md' 
                              : 'bg-white border border-green-500 text-green-600 hover:bg-green-50'
                            }
                            disabled:opacity-40 disabled:cursor-not-allowed
                          `}
                        >
                          {seleccionadaMas ? '✓ MÁS' : 'MÁS'}
                        </button>
                        <button
                          onClick={() => handleSeleccionMenos(palabra.id)}
                          disabled={respuestaMas === palabra.id}
                          className={`
                            flex-1 py-2 px-4 rounded-md font-medium transition-all
                            ${seleccionadaMenos 
                              ? 'bg-red-500 text-white shadow-md' 
                              : 'bg-white border border-red-500 text-red-600 hover:bg-red-50'
                            }
                            disabled:opacity-40 disabled:cursor-not-allowed
                          `}
                        >
                          {seleccionadaMenos ? '✓ MENOS' : 'MENOS'}
                        </button>
                      </div>

                      {/* Indicador de selección */}
                      {(seleccionadaMas || seleccionadaMenos) && (
                        <div className="absolute -top-2 -right-2">
                          <CheckCircle className={`w-6 h-6 ${seleccionadaMas ? 'text-green-500' : 'text-red-500'}`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Feedback */}
              <AnimatePresence>
                {puedeAvanzar && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-green-50 rounded-lg p-4 border border-green-200"
                  >
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-green-900 mb-1">¡Excelente!</p>
                        <p className="text-sm text-green-800">
                          Has completado correctamente el ejemplo. Así es como debes responder los 28 grupos del test real.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {!puedeAvanzar && (
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      Debes seleccionar una característica como <strong>MÁS</strong> y otra como <strong>MENOS</strong> para continuar
                    </p>
                  </div>
                </div>
              )}

              {/* Botones */}
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={resetEjemplo}
                  className="flex-1"
                >
                  Reintentar
                </Button>
                <Button
                  onClick={() => setPaso(2)}
                  disabled={!puedeAvanzar}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  Continuar
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Paso 0: Pantalla de instrucciones
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-3xl w-full"
      >
        <Card className="border-t-4 border-t-purple-600">
          <div className="p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <Brain className="w-16 h-16 text-purple-600 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-neutral-800 mb-2">
                Test DISC de Personalidad
              </h1>
              <p className="text-xl text-neutral-600">
                Instrucciones de Evaluación
              </p>
            </div>

            {/* ¿Qué es DISC? */}
            <div className="bg-purple-50 rounded-lg p-6 border border-purple-200 mb-6">
              <h3 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                <Brain className="w-5 h-5" />
                ¿Qué es el Test DISC?
              </h3>
              <p className="text-purple-800 mb-3">
                El Test DISC evalúa 4 dimensiones de tu personalidad y estilo de comportamiento:
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <p className="font-bold text-red-800">D - Dominancia</p>
                  <p className="text-xs text-red-700">Determinación, resultados</p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <p className="font-bold text-yellow-800">I - Influencia</p>
                  <p className="text-xs text-yellow-700">Sociabilidad, entusiasmo</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded p-3">
                  <p className="font-bold text-green-800">S - Estabilidad</p>
                  <p className="text-xs text-green-700">Paciencia, cooperación</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="font-bold text-blue-800">C - Cumplimiento</p>
                  <p className="text-xs text-blue-700">Precisión, análisis</p>
                </div>
              </div>
            </div>

            {/* Cómo responder */}
            <div className="bg-neutral-50 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-neutral-900 mb-4">📋 ¿Cómo Responder?</h3>
              
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4 border-2 border-neutral-200">
                  <p className="font-medium text-neutral-800 mb-3">
                    En cada grupo de palabras, debes seleccionar:
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3 p-3 bg-green-50 rounded border border-green-200">
                      <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                        <Plus className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-green-800 mb-1">MÁS (+)</p>
                        <p className="text-sm text-green-700">
                          La palabra que <strong>MÁS</strong> te describe o representa
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-3 bg-red-50 rounded border border-red-200">
                      <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0">
                        <Minus className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-red-800 mb-1">MENOS (-)</p>
                        <p className="text-sm text-red-700">
                          La palabra que <strong>MENOS</strong> te describe o representa
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>Ejemplo:</strong> Si ves las palabras "Enérgico", "Paciente", "Analítico", "Persuasivo":
                  </p>
                  <ul className="mt-2 space-y-1 text-sm text-blue-700">
                    <li>• Marca <strong>+</strong> en la que MÁS te representa</li>
                    <li>• Marca <strong>-</strong> en la que MENOS te representa</li>
                    <li>• Las otras 2 quedan sin marcar</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Consejos */}
            <div className="bg-neutral-50 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-neutral-900 mb-3">💡 Consejos Importantes:</h3>
              <ul className="space-y-2 text-neutral-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Sé honesto:</strong> No hay respuestas correctas o incorrectas</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Primera impresión:</strong> No pienses demasiado cada respuesta</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Contexto laboral:</strong> Responde pensando en tu comportamiento profesional</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Obligatorio:</strong> Debes marcar + y - en cada grupo antes de continuar</span>
                </li>
              </ul>
            </div>

            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200 mb-6">
              <p className="text-sm text-amber-800 text-center">
                <strong>⏱️ Tiempo estimado:</strong> 10-15 minutos · <strong>📊 Total:</strong> 28 grupos de palabras
              </p>
            </div>

            <Button
              onClick={() => setPaso(1)}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-lg py-3"
            >
              Ver Ejemplo Práctico
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

export default PantallaInstruccionesDisc;
